import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getOrderQuery } from '@/api/queries';
import { FirebaseClient } from '@/api/firebaseClient';
import { Container } from '@/components';

const client = new FirebaseClient();

export const OrderDetails = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const { data } = useQuery(getOrderQuery(orderId ?? ''));

  if (!data) return null;
  const { order, tickets } = data;
  const paid = Boolean(order.paidAt);
  const serviceFee = order.serviceFee ?? 0;
  const hasFee = serviceFee > 0;
  const total = order.total ?? order.price;

  const makePayment = async () => {
    const res = await client.getPaymentUrl(order.orderId);
    location.href = res.data.url;
  };

  if (order.canceledAt) {
    return (
      <Container max={760}>
        <h1 className="font-disp text-5xl uppercase text-ww-charcoal">{t('orderCanceled')}</h1>
        <div
          className="mt-6 p-6"
          style={{
            background: 'var(--paper-50)',
            border: '2px solid var(--ww-orange)',
            boxShadow: '5px 5px 0 var(--ww-orange)',
          }}>
          <h2 className="font-sans text-xl font-bold text-ww-orange">{t('orderCanceled')}</h2>
          <p className="mt-2 font-sans text-ink-soft">{t('ticketCanceled_message')}</p>
          <dl className="mt-4 font-mono text-sm">
            <dt className="text-ink-soft">{t('orderNumber')}</dt>
            <dd className="mb-2">{order.orderId}</dd>
            <dt className="text-ink-soft">E-Mail</dt>
            <dd>{order.email}</dd>
          </dl>
        </div>
      </Container>
    );
  }

  return (
    <Container max={920}>
      <div className="ww-eyebrow" style={{ color: 'var(--ww-orange)' }}>
        {t('orderStatus')}
      </div>
      <h1 className="mb-6 mt-2 font-disp text-5xl uppercase text-ww-charcoal">{t('your_order')}</h1>

      {!paid ? (
        <div
          className="mb-7 flex flex-wrap items-center justify-between gap-5 p-6"
          style={{
            background: 'var(--ww-orange)',
            color: 'var(--paper-50)',
            border: '3px solid var(--ww-charcoal)',
            boxShadow: '5px 5px 0 var(--ww-charcoal)',
          }}>
          <div>
            <div className="font-sans text-[22px] font-extrabold uppercase tracking-[0.02em]">
              {t('paymentPending')}
            </div>
            <p className="mt-1 font-sans text-sm text-paper-100">{t('paymentPending_message')}</p>
          </div>
          <button
            type="button"
            onClick={makePayment}
            className="ww-btn shrink-0"
            style={{ background: 'var(--paper-50)' }}>
            {t('payNow')} →
          </button>
        </div>
      ) : null}

      <dl className="m-0">
        {paid ? (
          <div
            className="flex items-baseline justify-between gap-4 py-3"
            style={{ borderBottom: '1px solid var(--paper-300)' }}>
            <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
              {t('paymentReceivedOn')}
            </dt>
            <dd className="m-0 font-sans text-lg font-medium text-ww-charcoal">
              {Intl.DateTimeFormat('de-DE').format(new Date(order.paidAt!))}
            </dd>
          </div>
        ) : null}
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
            {t('orderNumber')}
          </dt>
          <dd className="m-0 font-mono text-lg font-medium text-ww-charcoal">{order.orderId}</dd>
        </div>
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">E-Mail</dt>
          <dd className="m-0 font-sans text-lg font-medium text-ww-charcoal">{order.email}</dd>
        </div>
        {hasFee ? (
          <>
            <div
              className="flex items-baseline justify-between gap-4 py-3"
              style={{ borderBottom: '1px solid var(--paper-300)' }}>
              <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
                {t('orderPrice')}
              </dt>
              <dd className="m-0 font-mono text-lg font-medium text-ww-charcoal">
                {order.price} €
              </dd>
            </div>
            <div
              className="flex items-baseline justify-between gap-4 py-3"
              style={{ borderBottom: '1px solid var(--paper-300)' }}>
              <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
                {t('service_fee', { percent: order.serviceFeePercent })}
              </dt>
              <dd className="m-0 font-mono text-lg font-medium text-ww-charcoal">{serviceFee} €</dd>
            </div>
          </>
        ) : null}
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
            {hasFee ? t('grand_total') : t('orderPrice')}
          </dt>
          <dd className="m-0 font-mono text-3xl font-bold text-ww-orange">{total} €</dd>
        </div>
      </dl>

      <h2 className="mb-4 mt-8 font-sans text-lg font-bold uppercase tracking-[0.06em] text-ww-charcoal">
        {t('tickets')}
      </h2>
      <div className="flex flex-col gap-3">
        {tickets.map((ticket) => (
          <div
            key={ticket.ticketId}
            className="flex items-center justify-between gap-4 p-4"
            style={{ background: 'var(--paper-50)', border: '2px solid var(--ww-charcoal)' }}>
            <div className="flex flex-wrap items-baseline gap-3.5">
              <Link
                to={`/tickets/${ticket.ticketId}`}
                className="font-sans text-lg font-semibold text-ww-charcoal no-underline">
                {ticket.name}
              </Link>
              <span className="font-mono text-[13px] text-ink-soft">{ticket.ticketType.label}</span>
              <Link
                to={`/tickets/${ticket.ticketId}`}
                className="font-mono text-xs uppercase tracking-[0.06em] text-ww-orange">
                {t('showTicket')} →
              </Link>
            </div>
            <span className="font-mono text-xl font-bold text-ww-charcoal">
              {ticket.ticketType.price} €
            </span>
          </div>
        ))}
      </div>
    </Container>
  );
};
